import { useState, useCallback } from 'react'
import { INewTrailer } from 'apps/fleet-planner/schema/TrailerSchema'
import * as Types from 'apps/fleet-planner/types'
import * as Api from 'apps/fleet-planner/utils/api'

interface IUseDriverApiState {
  error?: string
  isLoading: boolean
  resetError: () => void
  /**
   * PUT request to update the trailer information
   */
  updateTrailer: (
    trailerId: string,
    changes: Partial<Types.ITrailer>
  ) => Promise<Types.ITrailer | undefined>
  /**
   * POST request to create new trailer
   */
  createNewTrailer: (trailer: INewTrailer) => Promise<Types.ITrailer | undefined>
}

/**
 * Hook used to create/update trailer entities
 */
export const useTrailerApi = (): IUseDriverApiState => {
  const [isLoading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  const resetError = useCallback(() => setError(''), [])

  /**
   * Update trailer with new information
   */
  const updateTrailer = useCallback(async (trailerId: string, changes: Partial<Types.ITrailer>) => {
    setLoading(true)
    try {
      const { trailer } = await Api.updateTrailer(trailerId, changes)
      setError('')
      setLoading(false)
      return trailer
    } catch (e) {
      setError(e)
      setLoading(false)
      return undefined
    }
  }, [])

  /**
   * Create new driver entity
   */
  const createNewTrailer = useCallback(async (t: INewTrailer): Promise<
    Types.ITrailer | undefined
  > => {
    setLoading(true)
    try {
      const { trailer } = await Api.createNewTrailer(t)
      setLoading(false)
      setError('')
      return trailer
    } catch (e) {
      setError(e)
      setLoading(false)
      return undefined
    }
  }, [])

  return {
    updateTrailer,
    createNewTrailer,
    error,
    resetError,
    isLoading,
  }
}
